import { useState, useEffect, createContext, useContext } from 'react';
import { useDeviceInfo } from '../../hooks/useDeviceInfo.js';
import { jsx } from 'react/jsx-runtime';
import { usePrevious } from '../../hooks/usePrevious.js';

const MenuContext = /*#__PURE__*/createContext();

/**
 * The wrapper that provides context and focus management
 * for Menu content.
 */
const MenuProvider = _ref => {
  let {
    children,
    data,
    labels,
    options
  } = _ref;
  const {
    isBrowser
  } = useDeviceInfo();
  const htmlTag = isBrowser ? document.documentElement : null;
  const [isOpen, setIsOpen] = useState(false);
  const [direction, setDirection] = useState(htmlTag?.dir);
  const [menuData, setMenuData] = useState(data ? [data] : []);
  const [menuLevel, setMenuLevel] = useState(0);

  /** If the data passed changes, set state to match */
  useEffect(() => setMenuData([data]), [data]);

  /** Create a ref so components can ascertain movement up/down menu */
  const prevMenuLevel = usePrevious(menuLevel);

  /** Detect if the direction changes - https://stackoverflow.com/a/64350748 */
  useEffect(() => {
    const observer = new MutationObserver(mutationsList => {
      if (mutationsList.some(mutation => mutation.attributeName === 'dir')) {
        setDirection(htmlTag.dir);
      }
    });
    observer.observe(htmlTag, {
      attributes: true
    });
    return () => observer.disconnect();
  }, [htmlTag]);
  const resetMenu = () => {
    setMenuLevel(0);
    setMenuData([data]);
  };
  const menuState = {
    labels,
    options,
    isOpen,
    setIsOpen,
    menuData,
    setMenuData,
    menuLevel,
    setMenuLevel,
    prevMenuLevel,
    direction,
    setDirection,
    resetMenu
  };
  return /*#__PURE__*/jsx(MenuContext.Provider, {
    value: menuState,
    children: children
  });
};
/**
 * Hook for consuming `MenuContext`: will throw an
 * error if used outside of a `MenuProvider`.
 *
 */
const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

export { MenuProvider, useMenu };
